import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  FormControl,
  Typography,
  useTheme,
  Tooltip,
} from "@mui/material";
import { useField, ErrorMessage } from "formik";
import FormikLabel from "./FormikLabel";
import { ErrorStyle } from "./ErrorStyle";
import { FileUploadOutlinedIcon } from "@/icons";

const FormikFileUpload = ({
  placeholder = " Upload File",
  name = "file",
  label = " Upload File",
  required = false,
  disabled = false,
  width = "100%",
  nameLength = 30,
  handleChange: customHandleChange,
  acceptedFileFormats = [],
  startIcon = <FileUploadOutlinedIcon />,
  endIcon,
  ...props
}) => {
  const [field, , helpers] = useField({ name, type: "file" });
  const [fileError, setFileError] = useState("");
  const theme = useTheme();

  // Unique input ID
  const uniqueId = useMemo(
    () => `${name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    [name]
  );

  // Truncate filename
  const truncateFileName = (fileName = "", maxLength = nameLength) => {
    if (!fileName) return "";
    return fileName.length <= maxLength
      ? fileName
      : fileName.substring(0, maxLength) + "...";
  };

  const handleFileChange = (event) => {
    const file = event.currentTarget.files[0];

    if (!file) {
      helpers.setValue(null);
      return;
    }

    const fileNameLower = file.name.toLowerCase();
    const parts = fileNameLower.split(".");

    // 🚫 Prevent double extension (abc.pdf.exe)
    if (parts.length > 2) {
      helpers.setValue(null);
      setFileError("Double extension files are not allowed.");
      return;
    }

    const fileExt = parts.pop();

    if (acceptedFileFormats.length > 0) {
      const isValid = acceptedFileFormats.some((format) => {
        return (
          file.type.includes(format) ||
          fileExt === format.toLowerCase() ||
          (format === "csv" && fileExt === "csv") ||
          (format === "xls" && (fileExt === "xls" || fileExt === "xlsx"))
        );
      });

      if (!isValid) {
        helpers.setValue(null);
        setFileError(
          `Please upload a valid file type: ${acceptedFileFormats.join(", ")}`
        );
        return;
      }
    }

    helpers.setValue(file);
    setFileError("");

    if (customHandleChange) {
      customHandleChange(event, file, helpers);
    }

    // Reset input to allow selecting same file again
    event.target.value = "";
  };

  const fileName = useMemo(() => {
    if (field.value) {
      return typeof field.value === "string" ? field.value : field.value.name;
    }
    return "";
  }, [field.value]);

  const truncatedFileName = truncateFileName(fileName);

  return (
    <FormControl variant="outlined" sx={{ width, ...props.sx }}>
      <FormikLabel text={label} required={required} />
      <Button
        component="label"
        startIcon={startIcon}
        endIcon={endIcon}
        fullWidth
        sx={{
          ...props.sx,
          display: "flex",
          justifyContent: "flex-start",
          border: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary,
          textAlign: "left",
          alignItems: "center",
          borderRadius: "8px",
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
          "&:disabled": {
            backgroundColor: theme.palette.action.disabled,
          },
        }}
      >
        <input
          id={uniqueId}
          type="file"
          hidden
          onChange={handleFileChange}
          disabled={disabled}
          accept={acceptedFileFormats.join(",")}
          {...props}
          style={{ ...props.sx }}
        />
        <Tooltip title={fileName || placeholder}>
          <span>{truncatedFileName || placeholder}</span>
        </Tooltip>
      </Button>

      {fileError && (
        <Typography variant="body2" color="error" sx={{ marginTop: 1 }}>
          {fileError}
        </Typography>
      )}
      <div style={ErrorStyle}>
        <ErrorMessage name={name} />
      </div>
    </FormControl>
  );
};

FormikFileUpload.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  width: PropTypes.string,
  nameLength: PropTypes.number,
  handleChange: PropTypes.func,
  acceptedFileFormats: PropTypes.arrayOf(PropTypes.string),
  startIcon: PropTypes.element,
  endIcon: PropTypes.element,
};

export default FormikFileUpload;
